/**
 * Точные числа лаборатории графиков.
 *
 * Коэффициент хранится нормализованной рациональной парой, а не `number`.
 * Причина не в педантизме: подписи и эквивалентные формы считаются из
 * коэффициентов, и на `number` парабола `y = 0.1(x - 0.3)² + 0.2` печатает
 * общую форму с `C = 0.20900000000000002`. Ученик видит мусор там, где мы
 * обещали ему связь коэффициента с графиком.
 *
 * В `number` значение переводится только для рисования — там точность уже
 * ограничена пикселем.
 */

export type ExactNumber = { numerator: number; denominator: number };

/**
 * Потолок знаменателя. Выше него пара перестаёт быть «простой дробью», ради
 * которой всё затевалось, и начинает рисковать переполнением при умножении
 * (`C = ah² + k` перемножает три коэффициента). За потолком число заменяется
 * ближайшей дробью с коротким знаменателем — точность рисунка от этого не
 * страдает, а арифметика остаётся в безопасных целых.
 */
const MAX_DENOMINATOR = 1_000_000;

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y > 0) {
    const rest = x % y;
    x = y;
    y = rest;
  }
  return x || 1;
}

/** Ближайшая дробь с ограниченным знаменателем — цепные дроби. */
export function fromNumber(value: number, maxDenominator = MAX_DENOMINATOR): ExactNumber {
  if (!Number.isFinite(value)) throw new Error("нечисловое значение");
  const sign = value < 0 ? -1 : 1;
  let rest = Math.abs(value);
  let previousNumerator = 0;
  let previousDenominator = 1;
  let numerator = 1;
  let denominator = 0;
  for (let step = 0; step < 64; step++) {
    const whole = Math.floor(rest);
    const nextNumerator = whole * numerator + previousNumerator;
    const nextDenominator = whole * denominator + previousDenominator;
    if (nextDenominator > maxDenominator) break;
    previousNumerator = numerator;
    previousDenominator = denominator;
    numerator = nextNumerator;
    denominator = nextDenominator;
    const fraction = rest - whole;
    if (fraction < 1e-12) break;
    rest = 1 / fraction;
  }
  if (denominator === 0) return { numerator: Math.round(value), denominator: 1 };
  return { numerator: sign * numerator, denominator };
}

/** Нормализация: знак в числителе, сокращение, защита от переполнения. */
function make(numerator: number, denominator: number): ExactNumber {
  if (denominator === 0) throw new Error("знаменатель не может быть нулём");
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator)) {
    throw new Error("нечисловое значение");
  }
  if (
    Math.abs(numerator) > Number.MAX_SAFE_INTEGER ||
    Math.abs(denominator) > Number.MAX_SAFE_INTEGER
  ) {
    return fromNumber(numerator / denominator);
  }
  const sign = denominator < 0 ? -1 : 1;
  const divisor = gcd(numerator, denominator);
  const result = {
    numerator: (sign * numerator) / divisor,
    denominator: (sign * denominator) / divisor,
  };
  if (result.denominator > MAX_DENOMINATOR) {
    return fromNumber(result.numerator / result.denominator);
  }
  return result;
}

function decimalsOf(value: number): number {
  const text = String(value);
  const dot = text.indexOf(".");
  if (dot < 0) return text.includes("e") || text.includes("E") ? 9 : 0;
  return Math.min(9, text.length - dot - 1);
}

/**
 * Точное число из пары. Дробные аргументы допускаются: `exact(1.5)` домножает
 * обе части на степень десяти, а не теряет хвост.
 */
export function exact(numerator: number, denominator = 1): ExactNumber {
  const shift = 10 ** Math.max(decimalsOf(numerator), decimalsOf(denominator));
  return make(Math.round(numerator * shift), Math.round(denominator * shift));
}

export const ZERO: ExactNumber = { numerator: 0, denominator: 1 };
export const ONE: ExactNumber = { numerator: 1, denominator: 1 };

export function toNumber(value: ExactNumber): number {
  return value.numerator / value.denominator;
}

export function addExact(a: ExactNumber, b: ExactNumber): ExactNumber {
  return make(a.numerator * b.denominator + b.numerator * a.denominator, a.denominator * b.denominator);
}

export function subExact(a: ExactNumber, b: ExactNumber): ExactNumber {
  return make(a.numerator * b.denominator - b.numerator * a.denominator, a.denominator * b.denominator);
}

export function mulExact(a: ExactNumber, b: ExactNumber): ExactNumber {
  return make(a.numerator * b.numerator, a.denominator * b.denominator);
}

/** Деление на ноль возвращает `null`: у вызывающего всегда есть ветка «не определено». */
export function divExact(a: ExactNumber, b: ExactNumber): ExactNumber | null {
  if (b.numerator === 0) return null;
  return make(a.numerator * b.denominator, a.denominator * b.numerator);
}

export function negExact(a: ExactNumber): ExactNumber {
  return { numerator: -a.numerator, denominator: a.denominator };
}

export function absExact(a: ExactNumber): ExactNumber {
  return { numerator: Math.abs(a.numerator), denominator: a.denominator };
}

export function isZero(a: ExactNumber): boolean {
  return a.numerator === 0;
}

export function isNegative(a: ExactNumber): boolean {
  return a.numerator < 0;
}

export function equalsExact(a: ExactNumber, b: ExactNumber): boolean {
  return a.numerator === b.numerator && a.denominator === b.denominator;
}

export function compareExact(a: ExactNumber, b: ExactNumber): number {
  const left = a.numerator * b.denominator;
  const right = b.numerator * a.denominator;
  return left === right ? 0 : left < right ? -1 : 1;
}

/**
 * Сколько знаков после запятой нужно дроби, чтобы записаться десятичной.
 * `null` — десятичная запись бесконечна (знаменатель имеет простые делители
 * кроме 2 и 5), такое число печатается дробью.
 */
function finiteDecimals(denominator: number): number | null {
  let rest = denominator;
  let twos = 0;
  let fives = 0;
  while (rest % 2 === 0) {
    rest /= 2;
    twos++;
  }
  while (rest % 5 === 0) {
    rest /= 5;
    fives++;
  }
  return rest === 1 ? Math.max(twos, fives) : null;
}

function trimZeros(text: string): string {
  return text.includes(".") ? text.replace(/0+$/, "").replace(/\.$/, "") : text;
}

/**
 * Запись коэффициента для подписи и поля ввода.
 *
 * Целое печатается целым, `3/4` — как `0.75`, а `1/3` остаётся дробью: врать
 * округлением в подписи коэффициента нельзя, ученик сравнивает её с полем.
 */
export function formatExact(value: ExactNumber, maxDecimals = 4): string {
  if (value.denominator === 1) return String(value.numerator);
  const digits = finiteDecimals(value.denominator);
  if (digits !== null && digits <= maxDecimals) {
    return trimZeros((value.numerator / value.denominator).toFixed(digits));
  }
  return `${value.numerator}/${value.denominator}`;
}

/** Приближённая запись для иррациональных координат (нули параболы). */
export function formatApprox(value: number, maxDecimals = 2): string {
  if (!Number.isFinite(value)) return "не определено";
  return trimZeros(value.toFixed(maxDecimals));
}

export type ParseOutcome =
  | { ok: true; value: ExactNumber }
  | { ok: false; error: string };

const NUMBER = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;

function parsePart(text: string): number | null {
  if (!NUMBER.test(text)) return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/**
 * Разбор ввода коэффициента: целое, десятичное с точкой или запятой, простая
 * дробь. Ни выражений, ни JavaScript — поле коэффициента не калькулятор, а
 * произвольный парсер в объём первой версии не входит.
 */
export function parseExact(input: string): ParseOutcome {
  const text = input.trim().replace(/[−‒–—]/g, "-").replace(/,/g, ".");
  if (text === "") return { ok: false, error: "Введите число" };
  if (text.length > 24) return { ok: false, error: "Слишком длинная запись" };

  const parts = text.split("/");
  if (parts.length > 2) {
    return { ok: false, error: "Нужно число или дробь: 2, −1.5, 3/4" };
  }
  const numerator = parsePart(parts[0] ?? "");
  if (numerator === null) {
    return { ok: false, error: "Нужно число или дробь: 2, −1.5, 3/4" };
  }
  if (parts.length === 1) return { ok: true, value: exact(numerator) };

  const denominator = parsePart(parts[1] ?? "");
  if (denominator === null) {
    return { ok: false, error: "Нужно число или дробь: 2, −1.5, 3/4" };
  }
  if (denominator === 0) return { ok: false, error: "Знаменатель не может быть нулём" };
  return { ok: true, value: exact(numerator, denominator) };
}
